/**
 * DiceTypeCard - Card showing provider configuration for one dice type.
 *
 * Displays the dice type name, optional custom name, training status badge,
 * and a ProviderList to manage provider priority and toggles.
 * Shows a loading skeleton while provider config is being fetched.
 */

import { Loader2 } from 'lucide-react';
import type {
  DiceRegistration,
  ProviderConfigResponse,
  RecognitionProvider
} from '@dice-capture/shared/types/dice-registry';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ProviderList } from './ProviderList';

interface DiceTypeCardProps {
  /** The dice registration record from the store */
  registration: DiceRegistration;
  /** Provider config for this device+diceType, or undefined while loading */
  providerConfig: ProviderConfigResponse | undefined;
  /** Whether provider config is currently being fetched */
  isLoading: boolean;
  /** Whether a voter-list save (PUT) is currently in flight — drives the inline indicator */
  isSaving?: boolean;
  /** Called when the user changes the active provider list */
  onProviderChange: (activeProviders: RecognitionProvider[]) => void;
}

/**
 * Returns a badge variant based on registration status.
 */
function getStatusVariant(
  status: DiceRegistration['status']
): 'default' | 'secondary' | 'outline' | 'destructive' {
  switch (status) {
    case 'ready': return 'default';
    case 'training': return 'secondary';
    case 'uploading': return 'outline';
    case 'failed': return 'destructive';
    default: return 'outline';
  }
}

/**
 * Returns human-readable text for a registration status.
 */
function getStatusLabel(status: DiceRegistration['status']): string {
  switch (status) {
    case 'ready': return 'Ready';
    case 'training': return 'Training...';
    case 'uploading': return 'Uploading';
    case 'failed': return 'Failed';
    default: return status;
  }
}

/**
 * Skeleton placeholder shown while provider config loads.
 */
function ProviderListSkeleton() {
  return (
    <div className="space-y-1.5 animate-pulse">
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          className="h-9 rounded-md bg-muted/50"
        />
      ))}
    </div>
  );
}

/**
 * Card component for a single dice type showing its provider configuration.
 */
export function DiceTypeCard({
  registration,
  providerConfig,
  isLoading,
  isSaving = false,
  onProviderChange
}: DiceTypeCardProps) {
  const { diceType, diceName, status } = registration;
  const displayName = diceName ? `${diceType.toUpperCase()} — ${diceName}` : diceType.toUpperCase();

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">{displayName}</CardTitle>
          <div className="flex items-center gap-2">
            {isSaving && (
              <span
                className="flex items-center gap-1 text-xs text-muted-foreground"
                aria-live="polite"
              >
                <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
                Saving…
              </span>
            )}
            <Badge variant={getStatusVariant(status)}>
              {getStatusLabel(status)}
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading || !providerConfig || !providerConfig.availableProviders?.length ? (
          <ProviderListSkeleton />
        ) : (
          <ProviderList
            availableProviders={providerConfig.availableProviders}
            activeProviders={providerConfig.activeProviders}
            onChange={onProviderChange}
          />
        )}
      </CardContent>
    </Card>
  );
}
